import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { MappingEditor } from '@/features/mappings/MappingEditor';
import {
  type MappingSchemaBody,
  archiveMapping,
  getMapping,
  patchMapping,
} from '@/features/mappings/mappings-api';
import { getTemplate } from '@/features/templates/templates-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/mappings/$mappingId')({
  component: MappingDetailPage,
});

function MappingDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const { mappingId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mapping = useQuery({
    queryKey: ['mappings', 'detail', mappingId],
    queryFn: () => getMapping(mappingId),
    retry: false,
  });

  const template = useQuery({
    queryKey: ['templates', mapping.data?.mapping.templateId],
    queryFn: () => getTemplate(mapping.data?.mapping.templateId as string),
    enabled: !!mapping.data?.mapping.templateId,
  });

  const [name, setName] = useState('');
  const [schema, setSchema] = useState<MappingSchemaBody | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mapping.data) {
      setName(mapping.data.mapping.name);
      setSchema(mapping.data.mapping.schema);
    }
  }, [mapping.data]);

  const save = useMutation({
    mutationFn: () =>
      patchMapping(mappingId, {
        name: name !== mapping.data?.mapping.name ? name : undefined,
        schema: schema ?? undefined,
      }),
    onSuccess: (data) => {
      toast.success(t('mappings.detail.saveSuccess', { version: data.mapping.version }));
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      queryClient.setQueryData(['mappings', 'detail', mappingId], data);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('mappings.detail.saveFailed'));
    },
  });

  const archive = useMutation({
    mutationFn: () => archiveMapping(mappingId),
    onSuccess: () => {
      toast.success(t('mappings.detail.archiveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      const templateId = mapping.data?.mapping.templateId;
      if (templateId) {
        void navigate({
          to: '/app/templates/$id/mappings',
          params: { id: templateId },
        });
      } else {
        void navigate({ to: '/app/templates' });
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('mappings.detail.archiveFailed'));
    },
  });

  if (mapping.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }
  if (mapping.isError || !mapping.data || !schema) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('mappings.detail.notFoundTitle')}</AlertTitle>
        <AlertDescription>{t('mappings.detail.notFoundBody')}</AlertDescription>
      </Alert>
    );
  }

  const placeholders = template.data?.template.placeholders ?? [];
  const current = mapping.data.mapping;

  return (
    <div className="space-y-6">
      <Link
        to="/app/templates/$id/mappings"
        params={{ id: current.templateId }}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        {t('mappings.detail.backLink')}
      </Link>

      <PageHeader
        eyebrow={t('app.layout.nav.mappings', { defaultValue: 'Mapping' })}
        title={current.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="rounded-full">
              v{current.version}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('mappings.detail.updatedAt', {
                when: dateTime(current.updatedAt),
                defaultValue: 'Updated {{when}}',
              })}
            </span>
          </span>
        }
        actions={
          <Button
            variant="destructive"
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
          >
            {archive.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="size-4" aria-hidden />
            )}
            {t('mappings.detail.archiveButton')}
          </Button>
        }
      />

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-border/70 shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">{t('mappings.detail.settings.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="map-name">{t('mappings.detail.settings.nameLabel')}</Label>
            <Input id="map-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <MappingEditor placeholders={placeholders} value={schema} onChange={setSchema} />
        </CardContent>
        <CardFooter>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="bg-foreground text-background hover:opacity-95"
          >
            {save.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {t('mappings.detail.saveButton')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
